import { RowData } from "@tanstack/react-table";
import { AppTable } from "./types/table-features";
import { Input } from "@capacit-com/lara-law-shared-ui";
import { DataTableViewOptions } from "./data-table-view-options";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { ListFilter, Trash } from "lucide-react";
import { CustomColumnMeta } from "./types/custom-column-meta";
import { DataTableFacetedFilter } from "./filters/data-table-faceted-filter";
import { FilterComponentMap } from "./filters/config/filter-component-map";
import { LeveledFiltersConfig } from "./data-table";
import { FilterOption } from "@/models/base/Filter";
import { useEffect, useMemo, useRef, useState } from "react";
import { getInvalidLabel } from "@/config/datasources";
import { cn } from "@/lib/utils";
import { SearchTag } from "./search-tag";
import SearchSyntaxHelper from "./search-syntax-helper";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { buildFilterOptions } from "@/pages/legislation/utils/ministryFilterOptions";

/**
 * Stands for a root selection seen before the levels had it, so that it still
 * reads as a change once they do — or once the root is cleared, which would
 * otherwise look like no change at all.
 */
const UNRESOLVED_ROOT_SELECTION = Symbol("unresolved root selection");

type LevelFilterDef = LeveledFiltersConfig["levels"][string]["filters"][string];

/**
 * The part of a child filter's selection the given level can show: values that
 * are none of its options (or their aliases) are dropped, and `undefined` means
 * nothing is left. A level filter without options keeps the selection as is.
 *
 * Two sources can share a filter key with different options, like Dokumenttype
 * on Retsinformation and EUR-Lex. Without this, a switch carried
 * Retsinformation's document types over and EUR-Lex matched nothing (LAR-594).
 */
function selectionValidFor(
  value: unknown,
  definition: LevelFilterDef
): unknown {
  if (!definition.options?.length) return value;

  const valid = new Set<string>();
  for (const option of definition.options) {
    valid.add(typeof option === "string" ? option : option.value);
  }
  for (const [canonical, aliases] of Object.entries(
    definition.optionAliases ?? {}
  )) {
    valid.add(canonical);
    aliases.forEach((alias) => valid.add(alias));
  }

  if (Array.isArray(value)) {
    const kept = value.filter((v) => valid.has(String(v)));
    return kept.length > 0 ? kept : undefined;
  }
  return typeof value === "string" && !valid.has(value) ? undefined : value;
}

interface DataTableToolbarProps<TData extends RowData> {
  table: AppTable<TData>;
  search?: string;
  searchTags?: string[];
  handleAddSearchTag?: (searchTag: string) => void;
  handleRemoveSearchTag?: (index: number) => void;
  handleClearSearchTags?: () => void;
  handleSearchChange?: (searchValue: string) => void;
  filters?: Record<
    string,
    { label: string; value: string; icon?: React.ComponentType }[]
  >;
  alwaysShowFilters?: boolean;
  createNode?: React.ReactNode;
  leveledFilters?: LeveledFiltersConfig;
  onRootSelectionChange?: () => void;
  showSearchSyntaxHelp?: boolean;
  /** Applies the predefined filter selection. Tables without one leave it out,
   *  and then no such action is offered. */
  onApplyDefaultFilters?: () => void;
  searchActions?: React.ReactNode;
}

export function DataTableToolbar<TData extends RowData>({
  table,
  search,
  searchTags,
  handleAddSearchTag,
  handleRemoveSearchTag,
  handleClearSearchTags,
  handleSearchChange,
  filters,
  alwaysShowFilters = false,
  createNode,
  leveledFilters,
  onRootSelectionChange,
  showSearchSyntaxHelp = false,
  onApplyDefaultFilters,
  searchActions,
}: DataTableToolbarProps<TData>) {
  const isFiltered = table.state.columnFilters?.length > 0 || false;
  const [isAnyPopoverOpen, setIsAnyPopoverOpen] = useState(false);

  // --- Leveled filters (root + dynamic children) ---
  // --- Add refs/memos for root selection and all child keys ---
  const lastRootSelectionRef = useRef<
    string | undefined | typeof UNRESOLVED_ROOT_SELECTION
  >(undefined);
  const allChildKeys = useMemo(() => {
    if (!leveledFilters) return new Set<string>();
    const { levels } = leveledFilters;
    return new Set(
      Object.values(levels)
        .flatMap((lvl) => Object.keys(lvl.filters))
        .filter(Boolean)
    );
  }, [leveledFilters]);

  // Derive current root selection from table state (not from render) and clear children when it changes
  const rootSelected = useMemo(() => {
    if (!leveledFilters) return undefined;
    const rootId = leveledFilters.rootColumnId;
    const filters = table.state.columnFilters ?? [];
    const entry = filters.find((f) => f.id === rootId);
    const v = entry?.value as unknown;
    if (Array.isArray(v)) return v.length === 1 ? (v[0] as string) : undefined;
    return v as string | undefined;
  }, [leveledFilters, table.state.columnFilters]);

  useEffect(() => {
    if (!leveledFilters) {
      return;
    }
    if (lastRootSelectionRef.current !== rootSelected) {
      // Until the levels have the selected root there is nothing to check its
      // child filters against, and pruning would drop all of them. The Kilder
      // config starts out that way on a page load: it leaves a source out
      // until that source's feature flag is evaluated for the user, which is
      // how a shared link opened in a new tab lost its filters (LAR-553). The
      // change is handled once the level arrives, or once the root changes
      // again. An own-property check, as the root can come from a link.
      if (
        rootSelected &&
        !Object.prototype.hasOwnProperty.call(
          leveledFilters.levels,
          rootSelected
        )
      ) {
        lastRootSelectionRef.current = UNRESOLVED_ROOT_SELECTION;
        return;
      }

      // Get current column filters
      const currentFilters = table.state.columnFilters;

      // Get valid child keys for the new root selection
      const validChildKeys = new Set<string>();
      if (rootSelected && leveledFilters?.levels[rootSelected]) {
        const selectedLevel = leveledFilters.levels[rootSelected];
        Object.keys(selectedLevel.filters).forEach((objectKey) => {
          validChildKeys.add(objectKey);
        });
      }

      // Filter out any column filters that are not valid child keys of the new root
      const keptFilters = currentFilters.filter((filter) => {
        // Keep the root column filter
        if (filter.id === leveledFilters?.rootColumnId) {
          return true;
        }
        // Keep only filters that are valid child keys of the new root
        return validChildKeys.has(filter.id);
      });

      // On a switch from one Kilde to another, a kept filter may still hold the
      // previous Kilde's values; keep only those the new Kilde offers. Not on the
      // first resolve: filters restored from a link or the session already
      // belong to the root they come with.
      const previousRoot = lastRootSelectionRef.current;
      const switchedKilde =
        typeof previousRoot === "string" &&
        rootSelected !== undefined &&
        previousRoot !== rootSelected;
      const newLevel = rootSelected
        ? leveledFilters.levels[rootSelected]
        : undefined;
      const filteredFilters =
        switchedKilde && newLevel
          ? keptFilters.flatMap((filter) => {
              const definition = newLevel.filters[filter.id];
              if (filter.id === leveledFilters.rootColumnId || !definition) {
                return [filter];
              }
              const value = selectionValidFor(filter.value, definition);
              return value === undefined ? [] : [{ ...filter, value }];
            })
          : keptFilters;

      // Update the table with the filtered column filters
      if (JSON.stringify(filteredFilters) !== JSON.stringify(currentFilters)) {
        table.setColumnFilters(filteredFilters);
      }

      // Call the callback to reset pagination and sorting when root selection changes
      // This includes going from undefined to a value (no source selected to source selected)
      if (onRootSelectionChange) {
        onRootSelectionChange();
      }

      // Update the ref to track the current root selection
      lastRootSelectionRef.current = rootSelected;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootSelected, allChildKeys, onRootSelectionChange]);

  const renderLeveledFilters = () => {
    if (!leveledFilters) return null;

    const { rootColumnId, levels } = leveledFilters;
    const rootColumn = table.getColumn(rootColumnId);
    if (!rootColumn) return null;

    // Build options for root from the "levels" keys
    const rootOptions = Object.entries(levels).map(([levelKey, cfg]) => ({
      label: cfg?.displayName || levelKey,
      value: levelKey,
    }));

    // Determine current selection on the root column
    const selected = rootColumn.getFilterValue();
    const selectedSingle = Array.isArray(selected)
      ? undefined
      : (selected as string | undefined);
    const selectedValues = selectedSingle ? [selectedSingle] : [];

    // Find common filters (filters that exist in all sources with the same key)
    const commonFilters: Array<{
      key: string;
      displayName?: string;
      options?: (string | FilterOption)[];
      optionAliases?: Record<string, string[]>;
      type?: "Multi" | "Single";
    }> = [];

    const levelEntries = Object.entries(levels);
    if (levelEntries.length > 0) {
      // Get all filter keys from the first source
      const firstLevelFilters = levelEntries[0][1]?.filters || {};
      const firstLevelFilterKeys = Object.keys(firstLevelFilters);

      // Check which filters exist in all sources
      for (const filterKey of firstLevelFilterKeys) {
        const firstFilter = firstLevelFilters[filterKey];
        const existsInAll = levelEntries.every(([_, levelCfg]) => {
          const filter = levelCfg?.filters?.[filterKey];
          if (!filter) return false;
          // Check if options are the same (for common filters like status)
          const firstOptions = JSON.stringify(firstFilter.options || []);
          const otherOptions = JSON.stringify(filter.options || []);
          return firstOptions === otherOptions;
        });

        if (existsInAll && firstFilter) {
          commonFilters.push({
            key: filterKey,
            displayName: firstFilter.displayName,
            options: firstFilter.options,
            optionAliases: firstFilter.optionAliases,
            type: firstFilter.type,
          });
        }
      }
    }

    // Render common filters (always shown)
    const commonFilterNodes = commonFilters.map((f) => {
      const col = table.getColumn(f.key);
      if (!col) return null;

      // Normalize options to {label,value}[] when provided
      const stringOptions = (f.options || []).map(
        (opt: string | FilterOption) =>
          typeof opt === "string" ? opt : opt.value
      );
      let opts = buildFilterOptions(f.key, stringOptions, f.optionAliases);

      // Transform Status filter options to use dynamic label based on selected source
      if (f.key === "status" && rootSelected) {
        // Capitalize first letter of source name to match getInvalidLabel expectations
        const capitalizedSource =
          rootSelected.charAt(0).toUpperCase() + rootSelected.slice(1);
        opts = opts.map((opt) => {
          if (opt.value === "Ikke-gældende") {
            return {
              ...opt,
              label: getInvalidLabel(capitalizedSource),
            };
          }
          return opt;
        });
      }

      const title =
        f.displayName ||
        ((col?.columnDef.meta as CustomColumnMeta)?.displayName as string) ||
        f.key;

      const t = (f.type || "Multi").toLowerCase();

      return (
        <DataTableFacetedFilter
          key={`lvl-common-${f.key}`}
          column={col}
          title={title}
          options={opts}
          filterKey={f.key}
          alwaysShowFilters={alwaysShowFilters}
          mode={t === "single" ? "single" : "multi"}
          isAnyPopoverOpen={isAnyPopoverOpen}
          setIsAnyPopoverOpen={setIsAnyPopoverOpen}
        />
      );
    });

    const root = (
      <DataTableFacetedFilter
        key={`lvl-root-${rootColumnId}`}
        column={rootColumn}
        title={
          (rootColumn.columnDef.meta as CustomColumnMeta)?.displayName ||
          rootColumnId
        }
        options={rootOptions}
        alwaysShowFilters={alwaysShowFilters}
        mode="single"
        isAnyPopoverOpen={isAnyPopoverOpen}
        setIsAnyPopoverOpen={setIsAnyPopoverOpen}
      />
    );

    // If exactly one level is selected, render its child filters (excluding common filters)
    let children: React.ReactNode = null;
    if (selectedValues.length === 1) {
      const selectedLevel = selectedValues[0] as string;
      const levelCfg = levels[selectedLevel];
      if (levelCfg && levelCfg.filters) {
        const commonFilterKeys = new Set(commonFilters.map((f) => f.key));
        children = Object.entries(levelCfg.filters)
          .filter(([objectKey]) => !commonFilterKeys.has(objectKey)) // Exclude common filters
          .map(([objectKey, f]) => {
            const col = table.getColumn(objectKey);
            if (!col) return null;

            // Normalize options with ministry alias metadata when configured
            const stringOptions = (f.options || []).map(
              (opt: string | FilterOption) =>
                typeof opt === "string" ? opt : opt.value
            );
            const opts = buildFilterOptions(
              objectKey,
              stringOptions,
              f.optionAliases
            );

            const title =
              f.displayName ||
              ((col?.columnDef.meta as CustomColumnMeta)
                ?.displayName as string) ||
              objectKey;

            const t = (f.type || "Multi").toLowerCase();

            return (
              <DataTableFacetedFilter
                key={`lvl-child-${objectKey}`}
                column={col}
                title={title}
                options={opts}
                filterKey={objectKey}
                alwaysShowFilters={alwaysShowFilters}
                mode={t === "single" ? "single" : "multi"}
                isAnyPopoverOpen={isAnyPopoverOpen}
                setIsAnyPopoverOpen={setIsAnyPopoverOpen}
              />
            );
          });
      }
    }

    return (
      <>
        {root}
        {commonFilterNodes}
        {children}
      </>
    );
  };

  return (
    <div className="flex items-end justify-between">
      <div className="flex flex-col flex-1 items-start space-x-2 justify-between">
        {handleSearchChange && (
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex items-center">
              <Input
                placeholder="Søg..."
                value={search}
                onChange={(event) => handleSearchChange(event.target.value)}
                onKeyDown={(event) => {
                  if (handleAddSearchTag) {
                    if (event.key === "Enter") {
                      handleAddSearchTag(
                        (event.target as HTMLInputElement).value
                      );
                    }
                  }
                }}
                className={cn(
                  "h-8 w-[150px] lg:w-[250px]",
                  showSearchSyntaxHelp && "pr-8"
                )}
              />
              {showSearchSyntaxHelp && <SearchSyntaxHelper />}
            </div>
            {searchActions}
            {searchTags &&
              searchTags.map((tag, index) => (
                <SearchTag
                  key={`${tag}-${index}`}
                  tag={tag}
                  onRemove={
                    handleRemoveSearchTag
                      ? () => handleRemoveSearchTag(index)
                      : undefined
                  }
                />
              ))}
            {searchTags && searchTags.length > 0 && handleClearSearchTags && (
              <>
                {/* Icon-only button for small screens with tooltip */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleClearSearchTags}
                      className="h-7 w-7 lg:hidden"
                    >
                      <Trash className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Ryd søgetags</p>
                  </TooltipContent>
                </Tooltip>

                {/* Button with text for large screens */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearSearchTags}
                  className="hidden h-7 px-2 text-xs lg:flex"
                >
                  Ryd søgetags
                </Button>
              </>
            )}
          </div>
        )}
        {(leveledFilters || filters) && (
          <div className="flex items-center gap-2 flex-wrap mt-2">
            {renderLeveledFilters()}

            {/* Faceted filters */}
            {filters &&
              Object.keys(filters).map((columnId) => {
                const column = table.getColumn(columnId);
                const options = filters[columnId];
                const filterType = (column?.columnDef?.meta as CustomColumnMeta)
                  ?.filterType;

                if (!column || !options) return null;

                if (filterType && filterType in FilterComponentMap) {
                  const FilterComponent =
                    FilterComponentMap[
                      filterType as keyof typeof FilterComponentMap
                    ];
                  if (!FilterComponent) return null;

                  return (
                    <FilterComponent
                      key={columnId}
                      column={column}
                      title={
                        (column.columnDef.meta as CustomColumnMeta)
                          ?.displayName || columnId
                      }
                      options={options}
                      alwaysShowFilters={alwaysShowFilters}
                      isAnyPopoverOpen={isAnyPopoverOpen}
                      setIsAnyPopoverOpen={setIsAnyPopoverOpen}
                    />
                  );
                }

                return (
                  <DataTableFacetedFilter
                    key={columnId}
                    column={column}
                    title={
                      (column.columnDef.meta as CustomColumnMeta)
                        ?.displayName || columnId
                    }
                    options={options}
                    isAnyPopoverOpen={isAnyPopoverOpen}
                    setIsAnyPopoverOpen={setIsAnyPopoverOpen}
                  />
                );
              })}
          </div>
        )}
      </div>
      {/* Widened only when the standard-filters action is there to hold: the
          fixed width keeps the filter area from reflowing as the buttons in
          this column come and go. */}
      <div
        className={cn(
          "flex flex-col items-center gap-2",
          onApplyDefaultFilters ? "w-28 lg:w-36" : "w-28"
        )}
      >
        {onApplyDefaultFilters && (
          <>
            {/* Icon-only button for small screens with tooltip */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={onApplyDefaultFilters}
                  className="h-8 w-8 lg:hidden"
                >
                  <ListFilter className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Anvend standardfiltre</p>
              </TooltipContent>
            </Tooltip>

            {/* Button with text for large screens */}
            <Button
              variant="outline"
              onClick={onApplyDefaultFilters}
              className="hidden h-8 px-2 lg:flex lg:px-3"
            >
              <ListFilter />
              Standardfiltre
            </Button>
          </>
        )}

        {isFiltered && (
          <Button
            variant="outline"
            onClick={() => {
              table.resetColumnFilters();
            }}
            className="h-8 px-2 lg:px-3"
          >
            <Trash />
            Ryd filtre
          </Button>
        )}

        <DataTableViewOptions table={table} />
      </div>
      {createNode && createNode}
    </div>
  );
}
